import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import { ShieldCheck } from 'lucide-react';
import FormError from '../../components/shared/FormError';
import ServerError from '../../components/shared/ServerError';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /\d/.test(p) },
  { label: 'One special character', test: (p: string) => /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/]/.test(p) },
];

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email ?? '';
  const setAuth = useAuthStore((s) => s.setAuth);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(newPassword) })),
    [newPassword],
  );

  const allRulesPassed = ruleResults.every((r) => r.passed);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRulesPassed && passwordsMatch && currentPassword.length > 0 && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPassed) {
      setError('Password does not meet all requirements.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from the current password.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        email,
        currentPassword,
        newPassword,
      });

      const payload = res.data.data ?? res.data;
      const { accessToken, refreshToken, user } = payload;
      if (accessToken && user) {
        setAuth(user, accessToken, refreshToken);
        if (user.tenantId) {
          localStorage.setItem('vqs_tenant_id', user.tenantId);
        } else {
          localStorage.removeItem('vqs_tenant_id');
        }
        toast.success('Password updated successfully.');
        navigate(user.role === 'SUPER_ADMIN' ? '/tenants' : '/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: Array<{ message?: string }> } } })?.response?.data?.errors?.[0]?.message;
      setError(msg ?? 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white overflow-hidden font-sans select-none">
      {/* Background Brand Anchor (Large Vertical Text) - Desktop Only */}
      <div className="absolute top-0 right-0 h-full hidden xl:flex items-center pointer-events-none pr-12">
        <h1 className="text-[min(12vw,14vh)] font-bold text-slate-100 uppercase tracking-tighter transform rotate-[-90deg] origin-right translate-y-1/2 leading-none opacity-70 whitespace-nowrap">
          Sales
        </h1>
      </div>

      <div className="container relative mx-auto px-6 lg:px-12 py-12 min-h-screen flex flex-col justify-center max-w-[1440px]">
        {/* Glow Effect Element */}
        <div
          className="absolute pointer-events-none opacity-60"
          style={{
            top: '-14%',
            left: '-28%',
            width: '80%',
            height: '26%',
            background: 'radial-gradient(circle at top left, #0099d681, #0000 60%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="grid lg:grid-cols-2 gap-20 2xl:gap-32 items-center w-full relative z-10">
          {/* Main Content Area (Sales Branding) */}
          <div className="max-w-2xl space-y-10">
            {/* Branding Section */}
            <div className="space-y-4">
              <img
                src="/truvideo-logo-dark.png"
                alt="TruVideo"
                className="w-auto h-8 lg:h-10"
              />
              <div className="h-0.5 w-12 bg-primary rounded-full"></div>
            </div>

            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                Security <br className="hidden sm:block" />
                <span className="text-primary font-black">Upgrade Hub.</span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-400 max-w-lg leading-relaxed font-medium italic">
                Update your credentials to ensure your account remains secure and your analytics data is protected.
              </p>
            </div>

            {/* Technical Metadata Labels */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-slate-100 max-w-lg">
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Security</div>
                <div className="text-3xl font-black text-primary">AES-256</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Protocol</div>
                <div className="text-3xl font-black text-primary">SSL/TLS</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Status</div>
                <div className="text-xl font-bold italic text-slate-400">Encrypted</div>
              </div>
            </div>

            <div className="flex gap-4 items-center pt-8">
              <div className="h-px w-12 bg-slate-200"></div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-bold">
                Access Security
              </span>
            </div>
          </div>

          {/* Form Section */}
          <div className="w-full flex justify-center lg:justify-start">
            <div className="w-full max-w-lg">
              <div className="w-full">
                <div className="mb-12 flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <ShieldCheck className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 tracking-[0.05em] uppercase">Change Password</h3>
                    <p className="text-slate-400 text-xs font-semibold tracking-wide uppercase mt-1">Your account requires a password update before you can continue</p>
                  </div>
                </div>

                <ServerError message={error} className="mb-8" />

                <form onSubmit={handleSubmit} noValidate className="space-y-8">
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        Business Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={email}
                          disabled
                          className="w-full h-14 rounded-xl border border-slate-100 bg-slate-50 px-6 text-sm font-medium text-slate-400 cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        Temporary Password <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full h-14 rounded-xl border border-slate-100 bg-slate-50 px-6 text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:bg-white focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/5 transition-all outline-none"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        New Password <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full h-14 rounded-xl border border-slate-100 bg-slate-50 px-6 text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:bg-white focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/5 transition-all outline-none"
                        />
                      </div>
                    </div>

                    {/* Password Strength Checklist */}
                    {newPassword.length > 0 && (
                      <div className="bg-slate-50 p-6 rounded-2xl space-y-3 border border-slate-100">
                        <span className="block text-[10px] font-bold text-slate-400 tracking-[0.2em] uppercase mb-2">
                          Requirements
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {ruleResults.map((rule, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className={`p-1 rounded-full ${rule.passed ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-400'}`}>
                                {rule.passed ? (
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                  <div className="h-3 w-3" />
                                )}
                              </div>
                              <span className={`text-[10px] font-bold tracking-wide uppercase ${rule.passed ? 'text-slate-700' : 'text-slate-400'}`}>
                                {rule.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        Confirm New Password <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••"
                          className={`w-full h-14 rounded-xl border px-6 text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-4 transition-all outline-none ${
                            confirmPassword.length > 0 && !passwordsMatch
                              ? 'border-red-300 bg-red-50/50 focus:border-red-400 focus:ring-red-500/5'
                              : 'border-slate-100 bg-slate-50 focus:bg-white focus:border-primary focus:ring-primary/5'
                          }`}
                          aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                        />
                      </div>
                      <FormError message={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : undefined} />
                      {passwordsMatch && (
                        <p className="mt-1.5 ml-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-green-600">
                          <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                          Passwords match
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full h-16 rounded-xl text-sm font-bold shadow-sm hover:shadow-lg hover:translate-y-[-1px] active:translate-y-[0px] transition-all bg-slate-900 text-white hover:bg-primary cursor-pointer flex items-center justify-center gap-3 uppercase tracking-[0.1em]"
                    disabled={!canSubmit}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Updating...</span>
                      </div>
                    ) : (
                      "Update Password & Login"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 lg:mt-0 lg:absolute lg:bottom-12">
          <p className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase">
            &copy; 2026 TruVideo &bull; All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
